import { useCallback, useRef, useState } from "react";
import { useObrasCommonReports } from "../components/hooks/obras_common_reports";
import { useObrasTopHeaderState } from "../components/hooks/obras_top_header_state";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useLocation } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { ObraHeaderInfo } from "../../domain/models/obra_header_info";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { IndicadoresServicosObra } from "../../domain/models/indicadores_servicos_obra";
import { IndicadoresServicosState, initialIndicadoresServicosState, useIndicadoresServicosFilters } from "../hooks/IndicadoresServicosObras/filters";
import { ChecklistServicoDropDown } from "../../domain/models/checklist_servico_dropdown";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { FiltrosIndicadoresServicosObraResponse } from "../workers/filtro.indicadores.worker";

/**
 * useIndicadoresServicosObrasViewModel
 */

export const useIndicadoresServicosObrasViewModel = () => {
  const {
    obras: {
      getObraHeaderInfoUseCase,
      getIndicadoresServicosObrasUseCase,
      getChecklistServicosDropDownUseCase
    },
  } = useDependencyInjection();

  const workerRef = useRef<Worker | null>(null);

  const obrasTopHeaderState = useObrasTopHeaderState();

  const debouncedHandler = useCreateDebounceHandler(750);

  const location = useLocation();

  const backOrNavigate = useBackOrNavigate();

  const { pushErrorNotification, setCustomLoading } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [idObra, setIdObra] = useState<number>();

  const [selectedObraHeaderInfo, setSelectedObraHeaderInfo] = useState<ObraHeaderInfo>();

  const [indicadoresServicosObra, setIndicadoresServicosObra] = useState<IndicadoresServicosObra>();
  const [filteredIndicadoresServicosObra, setFilteredIndicadoresServicosObra] = useState<IndicadoresServicosObra>();

  const [obrasReportState, initializeReportState] = useObrasCommonReports({ idObra });

  const [checklistServicosDropdown, setChecklistServicosDropdown] = useState<ChecklistServicoDropDown[]>();

  const filters = useIndicadoresServicosFilters();

  /**
   * Funções de carregamento
   */

  const filterIndicadoresServicosObra = useCallback((
    indicadores: IndicadoresServicosObra | undefined,
    f: IndicadoresServicosState,
  ) => {
    return new Promise<void>((accept, reject) => {
      if (typeof (Worker) !== "undefined") {
        if (workerRef.current) {
          workerRef.current.terminate();
        }

        const worker = new Worker(new URL("../workers/filtro.indicadores.worker.ts", import.meta.url), { type: "module" });

        workerRef.current = worker;

        worker.onmessage = (
          event: MessageEvent<FiltrosIndicadoresServicosObraResponse>
        ) => {
          setFilteredIndicadoresServicosObra(event.data as FiltrosIndicadoresServicosObraResponse);
          accept();
        };

        worker.onerror = error => {
          reject(error);
        };

        worker.postMessage({ indicadores, f });
      } else {
        try {
          const hasArea = f.area !== null && f.area.trim().length > 0;
          const hasAtividades = f.atividades !== null && f.atividades.length > 0;
          const hasStatus = f.status !== null && f.status !== 0;

          if (!indicadores) {
            setFilteredIndicadoresServicosObra(undefined);
            accept();
            return;
          }

          if (!hasArea && !hasAtividades && !hasStatus) {
            setFilteredIndicadoresServicosObra(indicadores);
            accept();
            return;
          }

          const areas = indicadores.areas.filter(x => {

            if (!hasArea && !hasStatus) return true;
            if (!hasArea && hasStatus) return x.servicos.some(x => x.status === f.status);

            return hasArea && hasStatus
              ? x.descricao.toLowerCase().includes(f.area!.toLowerCase()) && x.servicos.some(x => x.status === f.status)
              : x.descricao.toLowerCase().includes(f.area!.toLowerCase());
          });

          const servicos = hasStatus
            ? areas.map(x => x.servicos.filter(y => y.status === f.status).map(y => y.idChecklistServico)).flat()
            : [];

          const checklists = indicadores.checklists.filter(x => {

            if (!hasAtividades && !hasStatus) return true;
            if (!hasAtividades && hasStatus) return servicos.includes(x.idChecklistServico);

            return hasAtividades && hasStatus
              ? f.atividades!.includes(x.idChecklistServico) && servicos.includes(x.idChecklistServico)
              : f.atividades!.includes(x.idChecklistServico);
          });

          setFilteredIndicadoresServicosObra({ areas, checklists });
          accept();
        } catch (e: unknown) {
          reject(e);
        }
      }
    });
  }, [
  ]);

  const loadChecklistServicosDropDown = useCallback(async () => {
    try {
      const checklistServicosDropdown = await getChecklistServicosDropDownUseCase.getChecklistServicosDropDown();
      setChecklistServicosDropdown(checklistServicosDropdown);
    } catch (e: unknown) {
      throw new Error("Falha ao buscar dropdown de checklists!");
    }
  }, [
    getChecklistServicosDropDownUseCase,
  ]);

  const loadObraHeaderInfo = useCallback(async (id?: number) => {
    try {
      const filteredIdObra = id ?? idObra;
      const response = await getObraHeaderInfoUseCase.getObraHeaderInfo(filteredIdObra);
      setSelectedObraHeaderInfo(response);
    } catch (e: unknown) {
      setSelectedObraHeaderInfo(undefined);
      throw new Error('Falha ao buscar dados da obra!');
    }
  }, [
    idObra,
    getObraHeaderInfoUseCase,
  ]);

  const loadIndicadoresServicosObras = useCallback(async (id?: number) => {
    try {
      const filteredIdObra = id ?? idObra;
      const results = await getIndicadoresServicosObrasUseCase.getIndicadoresServicosObras(filteredIdObra);

      await filterIndicadoresServicosObra(
        results, useIndicadoresServicosFilters.getState());

      setIndicadoresServicosObra(results);
    } catch (e: unknown) {
      setIndicadoresServicosObra(undefined);
      throw new Error('Falha ao buscar dados dos indicadores de serviços da obra!');
    }
  }, [
    idObra,
    getIndicadoresServicosObrasUseCase,
    filterIndicadoresServicosObra,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get('id') ?? null;
      const locationIdObra = locationId ? Number(locationId) : undefined;

      setIdObra(locationIdObra);

      await loadChecklistServicosDropDown();

      await initializeReportState(locationIdObra);

      await loadObraHeaderInfo(locationIdObra);

      await loadIndicadoresServicosObras(locationIdObra);

    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/Obras_lista' });
  }, [
    backOrNavigate,
  ]);

  const handleReload = useCallback(async () => {
    if (idObra) {
      try {
        setLoading(true);

        await loadChecklistServicosDropDown();

        await initializeReportState(idObra);

        await loadObraHeaderInfo(idObra);

        await loadIndicadoresServicosObras(idObra);

      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    idObra,
    loadChecklistServicosDropDown,
    initializeReportState,
    loadObraHeaderInfo,
    loadIndicadoresServicosObras,
    pushErrorNotification,
  ]);

  const handleGoToAreaChecklistServico = useCallback((idObraAreaChecklist: number, idObraChecklistServico: number | null, openNewTab: boolean) => {
    if (idObra && idObraChecklistServico) {
      const url = `/ObraItensChecklistArea_cadastro?id=${idObra}&idChecklistArea=${idObraAreaChecklist}&idChecklistServico=${idObraChecklistServico}`;
      if (openNewTab) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        backOrNavigate(url, { alternativeRoute: `/IndicadoresServicosObras?id=${idObra}` });
      }
    }
  }, [
    idObra,
    backOrNavigate,
  ]);

  const handleAreaChange = useDebouncedCallback(debouncedHandler, async (area: string | null) => {
    useIndicadoresServicosFilters.setState({ area });
    return async () => {
      setCustomLoading(true);
      requestAnimationFrame(async () => {
        await filterIndicadoresServicosObra(indicadoresServicosObra, useIndicadoresServicosFilters.getState());
        requestAnimationFrame(async () => {
          setCustomLoading(false);
        });
      });
    }
  }, [
    indicadoresServicosObra,
    filterIndicadoresServicosObra,
    setCustomLoading,
  ]);

  const handleAtividadesChange = useDebouncedCallback(debouncedHandler, async (atividades: number[] | null) => {
    useIndicadoresServicosFilters.setState({ atividades });
    return async () => {
      setCustomLoading(true);
      requestAnimationFrame(async () => {
        await filterIndicadoresServicosObra(indicadoresServicosObra, useIndicadoresServicosFilters.getState());
        requestAnimationFrame(async () => {
          setCustomLoading(false);
        });
      });
    };
  }, [
    indicadoresServicosObra,
    filterIndicadoresServicosObra,
    setCustomLoading,
  ]);

  const handleStatusChange = useDebouncedCallback(debouncedHandler, async (status: number | null) => {
    useIndicadoresServicosFilters.setState({ status });
    return async () => {
      try {
        setCustomLoading(true);
        requestAnimationFrame(async () => {
          await filterIndicadoresServicosObra(indicadoresServicosObra, useIndicadoresServicosFilters.getState());
          requestAnimationFrame(async () => {
            setCustomLoading(false);
          });
        });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao atualizar filtros!'));
      }
    };
  }, [
    indicadoresServicosObra,
    filterIndicadoresServicosObra,
    pushErrorNotification,
    setCustomLoading,
  ]);

  const handleClearFilters = useCallback(async () => {
    setCustomLoading(true);
    useIndicadoresServicosFilters.setState({ ...initialIndicadoresServicosState });
    requestAnimationFrame(async () => {
      await filterIndicadoresServicosObra(indicadoresServicosObra, useIndicadoresServicosFilters.getState());
      requestAnimationFrame(async () => {
        setCustomLoading(false);
      });
    });
  }, [
    indicadoresServicosObra,
    filterIndicadoresServicosObra,
    setCustomLoading,
  ]);

  return {
    loading,
    errorMessage,
    selectedObraHeaderInfo: selectedObraHeaderInfo ?? null,
    filteredIndicadoresServicosObra,
    obrasTopHeaderState,
    obrasReportState,
    checklistServicosDropdown: checklistServicosDropdown ?? [],
    filters,
    handleBack,
    handleReload,
    handleGoToAreaChecklistServico,
    handleAreaChange,
    handleAtividadesChange,
    handleStatusChange,
    handleClearFilters,
  };
}